import { createSelector } from 'reselect'
import { canAddInsuranceToOrder, haveOrdersBeenFetched, sortOrdersByDeparture } from 'lib/order/orderUtils'
import isUpcoming, { isExperienceItemUpcoming, isUpcomingForPartnerVelocityEarnEligibility } from 'lib/order/isUpcoming'
import { isEmpty, nonNullable } from 'lib/array/arrayUtils'
import { isOrderUpsellable } from 'actions/UpsellAndAvailableCreditSnackbar/utils'
import config from 'constants/config'
import { VIRGIN_VELOCITY_END_DATE, VIRGIN_VELOCITY_START_DATE } from 'constants/partnerships'
import { VELOCITY_ENABLED_REGIONS } from 'constants/config/region'
import { isBedbankType } from 'lib/offer/offerTypes'
import { addDays } from 'lib/datetime/dateUtils'
export const getOrdersThatCanHaveInsuranceAdded = createSelector(
  (state: App.State) => state.orders.orders,
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.offer.offers,
  (orders, regionCode, offers) => {
    return Object.values(orders).filter(order => {
      const offer = offers[order.items[0]?.offerId]
      return canAddInsuranceToOrder(order, regionCode, offer)
    })
  },
)
export const getOrderOffers = createSelector(
  (state: App.State) => state.offer.offers,
  (state: App.State, offerIds: Array<string>) => offerIds,
  (offers: Record<string, App.Offer>, offerIds: Array<string>) => {
    return nonNullable(offerIds.map(offerId => offers[offerId]))
  },
)
export const getUpcomingOrders = createSelector(
  (state: App.State) => state.orders.orders,
  (orders) => Object.values(orders).filter(isUpcoming),
)
export const getOrderBookingNumbers = createSelector(
  (state: App.State) => state.orders.orders,
  (orders) => {
    return Object.values(orders).flatMap(({ id, bookingNumber, items, bedbankItems, tourItems, experienceItems, carHireItems, flightItems }) => {
      let locationSubheading = items[0]?.offer?.property?.locationSubheading
      if (isBedbankType(bedbankItems[0]?.offer?.type)) {
        locationSubheading = bedbankItems[0]?.offer?.property?.location
      }
      const itemObjects = items.map(item => ({
        id,
        startDate: item.reservation?.startDate,
        endDate: item.reservation?.endDate,
        bookingNumber: item.bookingNumber,
        latitude: item?.offer.property?.latitude,
        longitude: item?.offer.property?.longitude,
        locationSubheading,
        status: item.status,
      }))
      const bedbankItemObjects = bedbankItems.map(item => ({
        id,
        startDate: item.checkIn,
        endDate: item.checkOut,
        bookingNumber: item.bookingNumber,
        latitude: item?.offer.property?.latitude,
        longitude: item?.offer.property?.longitude,
        locationSubheading: item?.offer?.property?.location,
        status: item.status,
      }))
      const tourItemObjects = tourItems.map(item => ({
        id,
        bookingNumber: item.bookingId,
        status: item.status,
      }))
      const experienceItemObjects = experienceItems.map(item => ({
        id,
        bookingNumber: item.bookingNumber,
        status: item.status,
      }))
      const carHireItemObjects = carHireItems.map(item => ({
        id,
        bookingNumber,
        status: item.status,
      }))

      const flightItemObjects = flightItems.map(item => ({
        id,
        bookingNumber: item.pnrId,
        status: item.status,
        departureDate: item.departureDate,
      }))

      return [...itemObjects, ...bedbankItemObjects, ...tourItemObjects, ...experienceItemObjects, ...carHireItemObjects, ...flightItemObjects]
    })
  },
)
export const getPendingOrders = createSelector(
  (state: App.State) => state.orders.orders,
  (orders) => Object.values(orders).filter(order => order.status === 'pending'),
)
const getNonEmptyOrders = (orders: Array<App.Order>) => orders.filter(order =>
  !isEmpty(order.items) ||
  !isEmpty(order.bedbankItems) ||
  !isEmpty(order.flightItems) ||
  !isEmpty(order.tourItems) ||
  !isEmpty(order.experienceItems) ||
  !isEmpty(order.cruiseItems) ||
  !isEmpty(order.carHireItems) ||
  !isEmpty(order.transferItems) ||
  !isEmpty(order.customOfferItems),
)
export const getNextUpsellableOrder = createSelector(
  (state: App.State) => getUpcomingOrders(state),
  (orders): App.Order => {
    const sortedOrders = sortOrdersByDeparture(
      getNonEmptyOrders(orders).filter(
        (order) => order.status !== 'cancelled' && isOrderUpsellable(order),
      ),
    )
    return sortedOrders[0]
  },
)
export const getSortedOrdersByDeparture = createSelector(
  (state: App.State) => haveOrdersBeenFetched(state),
  (state: App.State) => state.orders.orders,
  (ordersFetched, orders) => {
    const allUnsortedOrders = getNonEmptyOrders(Object.values(orders))
    return {
      fetched: ordersFetched,
      data: sortOrdersByDeparture(allUnsortedOrders),
    }
  },
)
export const getUpcomingExperienceItems = createSelector(
  (state: App.State) => state.orders.orders,
  (orders) => {
    return Object.values(orders).filter(Boolean).flatMap(order => {
      return order.experienceItems.filter(item => isExperienceItemUpcoming(item, order))
    })
  },
)
const isLuxuryescapes: boolean = config.BRAND === 'luxuryescapes'
export function isOrderVelocityEligiblePostCheckout(order: App.Order) {
  return isLuxuryescapes &&
  VELOCITY_ENABLED_REGIONS.includes(order?.regionCode) &&
  isUpcomingForPartnerVelocityEarnEligibility(order) &&
  order.createdAt > VIRGIN_VELOCITY_START_DATE &&
  order.createdAt <= VIRGIN_VELOCITY_END_DATE
}

// QUEENSLAND CYCLONE SELECTORS - TO BE REMOVED ONCE BANNER IS REMOVED
function hotelIsInQueensland(location?: string): boolean {
  return !!(location?.toLowerCase().includes('queensland') || location?.toLowerCase().includes('qld'))
}

function isFlyingToOrFromQueensland(flightItem: App.OrderFlightItem) {
  const queenslandIntAirports = ['BNE', 'CNS', 'OOL', 'TSV', 'MCY']
  return flightItem.flights?.some(flight => {
    return (queenslandIntAirports.includes(flight.arrivalAirport ?? '') || queenslandIntAirports.includes(flight.departingAirport ?? '')) &&
    flight.departureDate && flight.arrivalDate &&
    isStartInNext14DaysOrCurrentBetweenStartAndEnd(flight.arrivalDate, flight.departureDate)
  })
}

function hasOrderInQueenslandAndInTimeFrame(order: App.Order) {
  const flightsItemsToQueensland = order.flightItems.filter(item => item.status !== 'cancelled').some(isFlyingToOrFromQueensland)
  const hotelItemsInQueensland = order.items.filter(item => item.status !== 'cancelled').some(item => hotelIsInQueensland(item.offer.property?.locationSubheading) &&
    item.reservation?.startDate && item.reservation?.endDate &&
    isStartInNext14DaysOrCurrentBetweenStartAndEnd(item.reservation?.startDate, item.reservation?.endDate),
  )

  const bedbankItemsInQueensland = order.bedbankItems.filter(item => item.status !== 'cancelled').some(item => hotelIsInQueensland(item.offer.property?.address?.stateProvinceCode) &&
    item.checkIn && item.checkOut &&
    isStartInNext14DaysOrCurrentBetweenStartAndEnd(item.checkIn.format('YYYY-MM-DD'), item.checkOut.format('YYYY-MM-DD')),
  )

  return flightsItemsToQueensland || hotelItemsInQueensland || bedbankItemsInQueensland
}

function isStartInNext14DaysOrCurrentBetweenStartAndEnd(startDate: string, endDate: string) {
  const currentDate = new Date()
  const next14Days = addDays(new Date(), 14)

  const startDateObj = new Date(startDate)
  const endDateObj = new Date(endDate)

  const isStartInNext14Days = startDateObj >= currentDate && startDateObj <= next14Days
  const isCurrentBetweenStartAndEnd = currentDate >= startDateObj && currentDate <= endDateObj

  return isStartInNext14Days || isCurrentBetweenStartAndEnd
}

export const isCustomerAffectedByQueenslandCyclone = createSelector(
  (state: App.State) => state.orders.orders,
  (orders) => {
    return Object.values(orders).some(hasOrderInQueenslandAndInTimeFrame)
  },
)
